import { Button, Tooltip, Badge } from '@mantine/core'
import { IconMessageCircle, IconCheck } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useFeedbackApi } from '@/hooks/useFeedbackApi'
import { notifications } from '@mantine/notifications'
import type { ComplianceDashboardData } from '@/types'

interface SendToExpertButtonProps {
  offense: ComplianceDashboardData
  size?: 'xs' | 'sm' | 'md'
}

export function SendToExpertButton({ offense, size = 'xs' }: SendToExpertButtonProps) {
  const { createFeedbackRequest, getFeedbackRequestByOffenseId } = useFeedbackApi()

  // Check if feedback request already exists
  const { data: existingRequest, refetch } = useQuery({
    queryKey: ['feedbackRequest', offense.offense_id],
    queryFn: () => getFeedbackRequestByOffenseId(offense.offense_id),
    retry: false,
  })

  const sendToExpertMutation = useMutation({
    mutationFn: () => {
      if (!offense.offense_id || !offense.search_id) {
        throw new Error('Missing offense or search ID')
      }
      return createFeedbackRequest({
        offense_id: offense.offense_id,
        search_id: offense.search_id,
        case_number: offense.case_number || undefined,
        priority: 'MEDIUM',
      })
    },
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Feedback request sent to experts',
        color: 'green',
      })
      refetch()
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to send feedback request',
        color: 'red',
      })
    },
  })

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent row click event
    sendToExpertMutation.mutate()
  }

  // If feedback request exists, show status
  if (existingRequest) {
    const statusColors = {
      PENDING: 'yellow',
      IN_PROGRESS: 'blue',
      COMPLETED: 'green',
      CANCELLED: 'gray',
    } as const

    const statusLabels = {
      PENDING: 'Pending Review',
      IN_PROGRESS: 'In Progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
    } as const

    return (
      <Tooltip label={`Feedback: ${statusLabels[existingRequest.feedback_status as keyof typeof statusLabels]}`}>
        <Badge
          size="lg"
          color={statusColors[existingRequest.feedback_status as keyof typeof statusColors]}
          leftSection={existingRequest.feedback_status === 'COMPLETED' ? <IconCheck size={14} /> : undefined}
        >
          {existingRequest.feedback_status === 'COMPLETED' ? 'Reviewed' : statusLabels[existingRequest.feedback_status as keyof typeof statusLabels]}
        </Badge>
      </Tooltip>
    )
  }

  return (
    <Tooltip label="Send this offense to an expert for feedback">
      <Button
        size={size}
        variant="light"
        leftSection={<IconMessageCircle size={14} />}
        onClick={handleClick}
        loading={sendToExpertMutation.isPending}
      >
        Send to Expert
      </Button>
    </Tooltip>
  )
}
